// src/utils/coreRequestBuilder.jsx
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { NAV_CAT_MAP, KNOTS_TO_MSEC } from "@/config/defaults";
dayjs.extend(utc);

function wrapLonLat(coords) {
  if (!Array.isArray(coords) || coords.length < 2) return coords;
  const [lon, lat] = coords;
  if (!Number.isFinite(lon) || !Number.isFinite(lat)) return coords;
  const wrapped = (((lon + 180) % 360) + 360) % 360 - 180;
  return [wrapped, lat];
}

// A point's legtype describes its outgoing leg; astar's enum is capitalised.
const astarLegType = (legtype) =>
  String(legtype ?? "").toLowerCase() === "rhumbline" ? "Rhumbline" : "Greatcircle";

// Hilfsfunktion: geschlossene Via Areas -> IDs
export function getViaAreaIdsNotOpen(cwViaAreasGeojson) {
  if (!cwViaAreasGeojson?.features) return [];
  return cwViaAreasGeojson.features
    .filter((feature) => feature.properties?.open === false)
    .map((feature) => feature.properties.id);
}

/**
 * Build a core API request body. Single source of truth for:
 *   /services/meteo-router       (algorithm: "pathfinder")
 *   /services/meteo-analyser     (algorithm: "analyser")
 *   /services/planroute-analyser (algorithm: "postprocessing_route_analyser")
 *
 * The first two return a flat JSON object body; the planroute variant wraps
 * the same property block in a GeoJSON FeatureCollection together with the
 * voyage's original waypoints (per the schema astar enforces server-side).
 *
 * Returns a JSON string in all cases — caller passes it straight to corePost.
 */
// Algorithm names per astar's request schema; echoed back in json_request.
export const ALGORITHM_PATHFINDER = "pathfinder";
export const ALGORITHM_PLANROUTE_ANALYSER = "postprocessing_route_analyser";

export function buildCoreRequest({
  algorithm = ALGORITHM_PATHFINDER,
  voyagePlannerPoints,
  calculatorDepartureTime,
  calculatorBasespeed,
  plannerVesselSettings,
  speedProfileData = null,
  consumptionProfileData = null,
  currentDraft = null,
  consumptionPrices = null,
  cii = null,
  fuelOptimisation = false,
  // ShippingDB vessel id: core derives the CII block from the master data.
  vesselId = null,
  windLimit = 0,
  waveLimit = 0,
  currentLimit = 0,
  activeEca = false,
  activeHra = false,
  activeJwla = false,
  activeCustomAreas = false,
  cwViaAreasGeojson,
  applyRnwSafety = false,
  erklaerbaer = false,
}) {
  // Astar schema expects strict UTC seconds: YYYY-MM-DDTHH:mm:ss+00:00
  const departureTime = dayjs(calculatorDepartureTime)
    .utc()
    .format("YYYY-MM-DDTHH:mm:ss[+00:00]");

  const departureCoords = wrapLonLat(
    voyagePlannerPoints?.features?.[0]?.geometry?.coordinates ?? [],
  );
  const departurePortId =
    voyagePlannerPoints?.features?.[0]?.properties?.portId ?? -1;

  // Last point, not the second: with a full waypoint list the arrival region
  // decides which approach-zone restrictions apply at the destination.
  // Map-edit recalcs pass the full geometry (calculated points included), so
  // pick the destination among the original points when there are any.
  const features = voyagePlannerPoints?.features ?? [];
  const originals = features.filter((f) => f?.properties?.type === "original_point");
  const endpoints = originals.length >= 2 ? originals : features;
  const arrivalFeature = endpoints.length >= 2 ? endpoints.at(-1) : undefined;
  const arrivalCoords = wrapLonLat(arrivalFeature?.geometry?.coordinates ?? []);
  const arrivalPortId = arrivalFeature?.properties?.portId ?? -1;

  // calculatorBasespeed comes in as knots (the voyage planner UI label is "kn").
  // Astar's request schema expects m/s on `basespeed_msec`; convert here.
  const basespeedKn = parseFloat(calculatorBasespeed);
  const basespeedMsec = Number.isFinite(basespeedKn)
    ? basespeedKn * KNOTS_TO_MSEC
    : NaN;

  const profiles = {
    wind: speedProfileData?.[0]
      ? {
          speedprofile_rows_angles: speedProfileData[0].speedprofile_rows_angles,
          speedprofile_columns_strengths:
            speedProfileData[0].speedprofile_columns_strengths,
          speedprofile_percent_speeds:
            speedProfileData[0].speedprofile_percent_speeds,
        }
      : null,
    wave: speedProfileData?.[1]
      ? {
          speedprofile_rows_angles: speedProfileData[1].speedprofile_rows_angles,
          speedprofile_columns_strengths:
            speedProfileData[1].speedprofile_columns_strengths,
          speedprofile_percent_speeds:
            speedProfileData[1].speedprofile_percent_speeds,
        }
      : null,
    // Comes request-ready from /consumption-profiles (`profile`) — passed
    // through unchanged.
    consumption: consumptionProfileData?.values?.length
      ? {
          ...consumptionProfileData,
          // Draft correction (ConsumptionTab): core only forwards the trio to
          // astar when currentDraft is present alongside the profile's
          // referenceDraft/draftDependency.
          ...(Number.isFinite(Number(currentDraft)) && Number(currentDraft) > 0
            ? { currentDraft: Number(currentDraft) }
            : {}),
        }
      : null,
  };

  const windLimitKMH = typeof windLimit === "number" ? windLimit * 1.852 : 0;
  const waveLimitM = typeof waveLimit === "number" ? waveLimit : 0;
  const currentLimitKMH =
    typeof currentLimit === "number" ? currentLimit * 1.852 : 0;

  const cargo = plannerVesselSettings?.cargo || {};
  const activeCargo = Object.keys(cargo).filter((key) => cargo[key] === true);
  const viaAreaIdsNotOpen = getViaAreaIdsNotOpen(cwViaAreasGeojson);
  const navigabilityCategoryValue =
    NAV_CAT_MAP[plannerVesselSettings?.navigabilityCategory] || 1;

  // Schema defaults: every required key must be present in the body even when
  // plannerVesselSettings is partially populated, otherwise JSON.stringify
  // drops undefined values and astar's JSON-Schema validator rejects.
  const num = (v, fallback = 0) => (Number.isFinite(Number(v)) ? Number(v) : fallback);
  const str = (v, fallback = "") => (typeof v === "string" && v ? v : fallback);
  const shiptypes = plannerVesselSettings?.shiptype
    ? [plannerVesselSettings.shiptype]
    : [];

  const props = {
    algorithm,
    // only the pathfinder model accepts this key; analyser/planroute schemas are strict
    ...(algorithm === "pathfinder" && erklaerbaer ? { erklaerbaer: true } : {}),
    // Fuel features: core translates these FE-only keys into astar's shape
    ...(consumptionPrices ? { consumption_prices: consumptionPrices } : {}),
    ...(cii ? { cii } : {}),
    ...(fuelOptimisation ? { fuel_optimisation: true } : {}),
    departure: {
      lonlat: departureCoords,
      datetime_utc: departureTime,
      portId: departurePortId,
    },
    arrival: {
      lonlat: arrivalCoords,
      portId: arrivalPortId,
    },
    vessel: {
      basespeed_msec: basespeedMsec,
      ...(Number.isFinite(Number(vesselId)) && Number(vesselId) > 0
        ? { id: Number(vesselId) }
        : {}),
    },
    wind_layer: { danger_ge_kmh: windLimitKMH },
    wave_layer: { danger_ge_meter: waveLimitM },
    current_layer: { danger_ge_kmh: currentLimitKMH },
    eca_variables_active: { is_active_eca: activeEca },
    hra_variables_active: { is_active_hra: activeHra },
    jwla_variables_active: { is_active_jwla: activeJwla },
    custom_areas_variables_active: { is_active_custom_areas: activeCustomAreas },
    profiles,
    config: { simplify_epsilon_meter: num(plannerVesselSettings?.simplifyEpsilonM, 4000) },
    rnw_variables_vessel: {
      length_meter_rnw_restrictions: num(plannerVesselSettings?.lengthM),
      beam_meter_rnw_restrictions: num(plannerVesselSettings?.beamM),
      maxdraft_meter_rnw_restrictions: num(plannerVesselSettings?.maxDraftM),
      gross_tonnage_rnw_restrictions: num(plannerVesselSettings?.grossTonnage),
      shiptypes_rnw_restrictions: shiptypes,
      nationality_rnw_restrictions: str(plannerVesselSettings?.nationality),
    },
    rnw_variables_voyage: {
      safety_depth_meter_rnw_legs: num(plannerVesselSettings?.safetyDepthM),
      navigability_category_limit_rnw_legs: navigabilityCategoryValue,
      air_draft_meter_rnw_legs: num(plannerVesselSettings?.airDraftM),
      via_area_ids_not_open_rnw_viaareas: viaAreaIdsNotOpen,
      loadstate_rnw_restrictions: str(plannerVesselSettings?.loadstate),
      cargos_rnw_restrictions: activeCargo,
    },
    rnw_variables_active: {
      is_active_rnw_restrictions: applyRnwSafety,
      is_active_rnw_legs: applyRnwSafety,
      is_active_rnw_viaareas: applyRnwSafety,
    },
  };

  if (algorithm === "postprocessing_route_analyser") {
    // Planroute analyser: astar consumes a GeoJSON FeatureCollection with one
    // json_request feature + N waypoint Point features. node_id and the real_*
    // fields must be present-but-may-be-null per the schema; we send null so
    // astar resnaps each waypoint to the currently-loaded RNW graph.
    const originalPoints = (voyagePlannerPoints?.features ?? []).filter(
      (f) => f?.properties?.type === "original_point",
    );
    // Preserve any node_id / real_* values the FE already has so astar can
    // skip the per-waypoint graph snap. The schema accepts integer or null;
    // fall back to null when the source feature doesn't carry the field.
    const intOrNull = (v) => (Number.isInteger(v) ? v : null);
    const numOrNull = (v) => (Number.isFinite(v) ? v : null);
    const waypointFeatures = originalPoints.map((f, i) => {
      const props = f.properties || {};
      return {
        type: "Feature",
        geometry: {
          type: "Point",
          coordinates: wrapLonLat(f.geometry?.coordinates ?? [0, 0]),
        },
        properties: {
          waypoint_number: i,
          leg_type: astarLegType(props.legtype ?? props.legType),
          basespeed_msec: basespeedMsec,
          node_id: intOrNull(props.node_id),
          real_vessel_hour: numOrNull(props.real_vessel_hour ?? props.realVesselHour),
          real_vessel_distance_km: numOrNull(
            props.real_vessel_distance_km ?? props.realVesselDistanceKm,
          ),
        },
      };
    });

    return JSON.stringify({
      type: "FeatureCollection",
      features: [
        {
          type: "Feature",
          geometry: null,
          properties: { type: "json_request", ...props },
        },
        ...waypointFeatures,
      ],
    });
  }

  return JSON.stringify(props);
}
